import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  TooltipItem,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import {
  Users,
  UserCheck,
  Shield,
  Crown,
  DollarSign,
  TrendingUp,
  CreditCard,
  Activity,
  FileText,
  Brain,
  Sparkles,
} from 'lucide-react';
import { SuperAdminMetrics, getSuperAdminMetrics } from '../services/adminMetricsService';

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

// Formatters
const formatNumber = (value: number) => new Intl.NumberFormat('es-ES').format(value);
const formatCurrency = (value: number) =>
  new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(value);
const formatTierLabel = (tier: string) => {
  if (tier === 'free') return 'Free';
  if (tier === 'pro') return 'Pro';
  if (tier === 'enterprise') return 'Enterprise';
  return tier;
};

// Stat Card Component - Stripe style (white bg + border)
interface StatCardProps {
  label: string;
  value: string | number;
  icon: React.ReactNode;
  sublabel?: string;
  iconBg: string;
  iconColor: string;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, icon, sublabel, iconBg, iconColor }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <p className="text-xs lg:text-sm font-medium text-slate-500 uppercase tracking-wide truncate">
          {label}
        </p>
        <p className="text-xl lg:text-2xl font-bold text-slate-900 mt-1">{value}</p>
        {sublabel && (
          <p className="text-xs text-slate-500 mt-1 truncate">{sublabel}</p>
        )}
      </div>
      <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${iconBg} ${iconColor} flex-shrink-0`}>
        {icon}
      </div>
    </div>
  </div>
);

// Section Header Component
const SectionHeader: React.FC<{ title: string; subtitle: string }> = ({ title, subtitle }) => (
  <div className="flex items-center justify-between mb-3">
    <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
    <span className="text-xs text-slate-400">{subtitle}</span>
  </div>
);

export default function SuperAdminDashboard() {
  const { t } = useTranslation(['app', 'common']);
  const [metrics, setMetrics] = useState<SuperAdminMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    const loadMetrics = async () => {
      setLoading(true);
      setError(null);
      const data = await getSuperAdminMetrics();
      if (!data) {
        setError(t('app:admin.errorLoadingMetrics'));
      }
      setMetrics(data);
      setLoading(false);
    };

    loadMetrics();
  }, [t]);

  // Chart data: Subscriptions by tier (count + MRR)
  const chartData = useMemo(() => {
    if (!metrics) return null;

    const tiers = ['free', 'pro', 'enterprise'];
    const counts = tiers.map(tier => metrics.activeSubscriptionCounts?.[tier] || 0);

    return {
      labels: tiers.map(formatTierLabel),
      datasets: [
        {
          label: t('app:admin.subscribersLabel'),
          data: counts,
          backgroundColor: [
            'rgba(148, 163, 184, 0.8)', // slate-400 for Free
            'rgba(59, 130, 246, 0.8)',  // blue-500 for Pro
            'rgba(16, 185, 129, 0.8)',  // emerald-500 for Enterprise
          ],
          borderColor: [
            'rgba(148, 163, 184, 1)',
            'rgba(59, 130, 246, 1)',
            'rgba(16, 185, 129, 1)',
          ],
          borderWidth: 1,
          borderRadius: 8,
          barThickness: 60,
        },
      ],
    };
  }, [metrics, t]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: { size: 14, weight: 'bold' as const },
        bodyFont: { size: 13 },
        callbacks: {
          label: function (context: TooltipItem<'bar'>) {
            const tier = context.label?.toLowerCase() || '';
            const count = context.parsed.y;
            const mrr = metrics?.mrrByTier?.[tier] || 0;
            return [t('app:admin.subscribersCount', { value: formatNumber(count) }), t('app:admin.mrrLabel', { value: formatCurrency(mrr) })];
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: { stepSize: 1, font: { size: 12 } },
        grid: { color: 'rgba(0, 0, 0, 0.05)' },
      },
      x: {
        ticks: { font: { size: 12 } },
        grid: { display: false },
      },
    },
  };

  // KPI metrics (financial)
  const kpiMetrics: StatCardProps[] = metrics ? [
    { label: t('app:admin.mrr'), value: formatCurrency(metrics.mrr ?? 0), icon: <DollarSign className="w-5 h-5" />, sublabel: t('app:admin.mrrSublabel'), iconBg: 'bg-emerald-100', iconColor: 'text-emerald-600' },
    { label: t('app:admin.arr'), value: formatCurrency(metrics.arr ?? 0), icon: <TrendingUp className="w-5 h-5" />, sublabel: t('app:admin.arrSublabel'), iconBg: 'bg-blue-100', iconColor: 'text-blue-600' },
    { label: t('app:admin.arpu'), value: formatCurrency(metrics.arpu ?? 0), icon: <CreditCard className="w-5 h-5" />, sublabel: t('app:admin.arpuSublabel'), iconBg: 'bg-indigo-100', iconColor: 'text-indigo-600' },
    { label: t('app:admin.subscribers'), value: formatNumber(metrics.paidSubscribers ?? 0), icon: <Users className="w-5 h-5" />, sublabel: t('app:admin.activeOrTrial'), iconBg: 'bg-amber-100', iconColor: 'text-amber-600' },
  ] : [];

  // Usage metrics
  const usageMetrics: StatCardProps[] = metrics ? [
    { label: t('app:admin.users_label'), value: formatNumber(metrics.totalUsers), icon: <Users className="w-5 h-5" />, iconBg: 'bg-blue-100', iconColor: 'text-blue-600' },
    { label: t('app:admin.clinicians'), value: formatNumber(metrics.totalClinicians), icon: <UserCheck className="w-5 h-5" />, iconBg: 'bg-emerald-100', iconColor: 'text-emerald-600' },
    { label: t('app:admin.testsPerMonth'), value: formatNumber(metrics.testsThisMonth), icon: <FileText className="w-5 h-5" />, sublabel: t('app:admin.clinicalActivity'), iconBg: 'bg-cyan-100', iconColor: 'text-cyan-600' },
    { label: t('app:admin.evolutivosPerMonth'), value: formatNumber(metrics.evolutivosThisMonth), icon: <Activity className="w-5 h-5" />, sublabel: t('app:admin.followUp'), iconBg: 'bg-violet-100', iconColor: 'text-violet-600' },
  ] : [];

  // AI usage metrics
  const aiMetrics: StatCardProps[] = metrics ? [
    { label: t('app:admin.aiTests'), value: formatNumber(metrics.aiTestsTotal ?? 0), icon: <Brain className="w-5 h-5" />, sublabel: t('app:admin.thisMonth', { value: formatNumber(metrics.aiTestsThisMonth ?? 0) }), iconBg: 'bg-cyan-100', iconColor: 'text-cyan-600' },
    { label: t('app:admin.aiEvolutivos'), value: formatNumber(metrics.aiEvolutivosTotal ?? 0), icon: <Sparkles className="w-5 h-5" />, sublabel: t('app:admin.thisMonth', { value: formatNumber(metrics.aiEvolutivosThisMonth ?? 0) }), iconBg: 'bg-violet-100', iconColor: 'text-violet-600' },
  ] : [];

  // Admin counts
  const adminMetrics: StatCardProps[] = metrics ? [
    { label: t('app:admin.admins'), value: formatNumber(metrics.totalAdmins), icon: <Shield className="w-5 h-5" />, iconBg: 'bg-indigo-100', iconColor: 'text-indigo-600' },
    { label: t('app:admin.superadmins'), value: formatNumber(metrics.totalSuperadmins), icon: <Crown className="w-5 h-5" />, iconBg: 'bg-rose-100', iconColor: 'text-rose-600' },
  ] : [];

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4 lg:p-6 shadow-sm">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full bg-indigo-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-indigo-700 mb-2">
            {t('app:admin.superadminBadge')}
          </div>
          <h2 className="text-lg lg:text-xl font-bold text-slate-900">
            {t('app:admin.saasVisibility')}
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            {t('app:admin.saasVisibilityDesc')}
          </p>
        </div>
        <div className="hidden lg:flex items-center gap-2 text-xs text-slate-500">
          <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
          {t('app:admin.healthyStatus')}
        </div>
      </div>

      {/* Loading state */}
      {loading && (
        <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-slate-500 text-sm">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
          {t('app:admin.loadingMetrics')}
        </div>
      )}

      {/* Error state */}
      {error && !loading && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Content */}
      {!loading && metrics && (
        <div className="space-y-6">
          {/* KPIs Financieros */}
          <section>
            <SectionHeader title={t('app:admin.estimatedRevenue')} subtitle={t('app:admin.eurMonthly')} />
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
              {kpiMetrics.map(({ label, value, icon, sublabel, iconBg, iconColor }) => (
                <StatCard key={label} label={label} value={value} icon={icon} sublabel={sublabel} iconBg={iconBg} iconColor={iconColor} />
              ))}
            </div>
          </section>

          {/* Métricas de Uso */}
          <section>
            <SectionHeader title={t('app:admin.usageMetrics')} subtitle={t('app:admin.currentMonth')} />
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
              {usageMetrics.map(({ label, value, icon, sublabel, iconBg, iconColor }) => (
                <StatCard key={label} label={label} value={value} icon={icon} sublabel={sublabel} iconBg={iconBg} iconColor={iconColor} />
              ))}
            </div>
          </section>

          {/* Uso de IA */}
          <section>
            <SectionHeader title={t('app:admin.aiUsage')} subtitle={t('app:admin.totalVsMonth')} />
            <div className="grid grid-cols-2 gap-3 lg:gap-4">
              {aiMetrics.map(({ label, value, icon, sublabel, iconBg, iconColor }) => (
                <StatCard key={label} label={label} value={value} icon={icon} sublabel={sublabel} iconBg={iconBg} iconColor={iconColor} />
              ))}
            </div>
          </section>

          {/* Admin counts */}
          <section>
            <SectionHeader title={t('app:admin.administration')} subtitle={t('app:admin.elevatedRoles')} />
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
              {adminMetrics.map(({ label, value, icon, sublabel, iconBg, iconColor }) => (
                <StatCard key={label} label={label} value={value} icon={icon} sublabel={sublabel} iconBg={iconBg} iconColor={iconColor} />
              ))}
            </div>
          </section>

          {/* Bar Chart: Subscriptions by Plan */}
          <section className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
            <SectionHeader title={t('app:admin.subscriptionsByPlan')} subtitle={t('app:admin.activePlusTrial')} />
            {chartData && chartData.datasets[0].data.some(v => v > 0) ? (
              <div className="h-64">
                <Bar data={chartData} options={chartOptions} />
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center">
                <p className="text-sm text-slate-500">{t('app:admin.noChartData')}</p>
              </div>
            )}
          </section>
        </div>
      )}

    </div>
  );
}
